import React, { useState } from 'react'
import FilterTrigger from './FilterTrigger'
import CategoryPanel from './panels/CategoryPanel'
import CharityPanel from './panels/CharityPanel'
import CausePanel from './panels/CausePanel'
import SortPanel from './panels/SortPanel'
import ChipsRow from './ChipsRow'
import MobileFilterDrawer from './MobileFilterDrawer'
import MobileSortDrawer from './MobileSortDrawer'
import { labelForCode as sortLabelForCode } from './sortOptions'
import LotTypeSeg from './LotTypeSeg'
import { FunnelIcon, SortIcon } from './icons'

// Variant A — inline dropdown rail.
// One trigger may be open at a time; clicking a trigger toggles it and
// closes any other. Each panel manages its own staged state and commits
// to model.filters on Apply.
//

// Applied (non-locked) selections per facet, for the "Category (2)" trigger
// labels and the phone Filters badge.
function facetCounts(model) {
  const f = model.filters || {}
  const count = (map, lockedMap = {}) => Object.keys(map || {}).filter((k) => map[k] && !lockedMap[k]).length
  return {
    category: count(f.departments && f.departments.selected, f.departments && f.departments.locked),
    charity: count(f.orgs && f.orgs.selected && f.orgs.selected.orgs, f.orgs && f.orgs.orgs_locked),
    cause: count(f.orgs && f.orgs.selected && f.orgs.selected.causes),
  }
}

export default function FilterToolbar({ model, changeState, search, onClearAll, showClearAll }) {
  const [openFacet, setOpenFacet] = useState(null)
  const [sortOpen, setSortOpen] = useState(false)
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false)
  const [mobileSortOpen, setMobileSortOpen] = useState(false)
  const counts = facetCounts(model)
  const appliedCount = counts.category + counts.charity + counts.cause

  function toggleFacet(key) {
    setSortOpen(false)
    setOpenFacet((prev) => (prev === key ? null : key))
  }
  function closeFacet() { setOpenFacet(null) }

  function renderPanel(key) {
    const common = { model, changeState, search, onClose: closeFacet }
    switch (key) {
      case 'category':
        return <CategoryPanel {...common} />
      case 'charity':
        return <CharityPanel {...common} />
      case 'cause':
        return <CausePanel {...common} />
      default:
        return null
    }
  }

  const sortLabel = sortLabelForCode(model, (model.filters.sort_by && model.filters.sort_by.query && model.filters.sort_by.query.code) || '')
  const facet = (key, label) => (
    <FilterTrigger
      multi
      label={label}
      count={counts[key]}
      isOpen={openFacet === key}
      onToggle={() => toggleFacet(key)}
      onClose={closeFacet}
    >
      {renderPanel(key)}
    </FilterTrigger>
  )

  // DEV-2980: drawn as the storefront's PLP filter bar (cb-storefront
  // plp/listing/Filter.tsx: FilterBar, FilterMetaRow, MobileFilter), styled by
  // www/storefront_chrome.css under .sfc. Lot type now sits here too, as the
  // storefront's "Show:" control, shared with the sticky bar.
  return (
    <div className="sfc" data-component="filter-toolbar">
      <div className="fb-bar sfc-desk">
        <div className="fb-bar-inner">
          <div className="fb-cluster">
            <span className="fb-label">Show:</span>
            <LotTypeSeg filters={model.filters} />
            {facet('category', 'Category')}
            {facet('charity', 'Charity')}
            {facet('cause', 'Cause')}
          </div>
          <div className="fb-cluster">
            <span className="fb-label">Sort by:</span>
            <FilterTrigger
              label={sortLabel}
              isOpen={sortOpen}
              onToggle={() => { setOpenFacet(null); setSortOpen((p) => !p) }}
              onClose={() => setSortOpen(false)}
              alignRight
            >
              <SortPanel
                model={model}
                changeState={changeState}
                search={search}
                onClose={() => setSortOpen(false)}
              />
            </FilterTrigger>
          </div>
        </div>
      </div>

      <div className="plpr-mfilter sfc-compact">
        <div className="plpr-mfilter-seg"><LotTypeSeg filters={model.filters} /></div>
        <div className="plpr-mfilter-actions">
          <button type="button" className="plpr-mfilter-btn" onClick={() => setMobileFilterOpen(true)}>
            <FunnelIcon /> Filters
            {appliedCount > 0 && <span className="plpr-mfilter-badge">{appliedCount}</span>}
          </button>
          <button type="button" className="plpr-mfilter-btn" onClick={() => setMobileSortOpen(true)}>
            <SortIcon /> {sortLabel}
          </button>
        </div>
      </div>

      {/* Chips show at every width; after the compact buttons, so they sit under them. */}
      <div>
        <ChipsRow
          model={model}
          changeState={changeState}
          search={search}
          onClearAll={onClearAll}
          showClearAll={showClearAll}
        />
      </div>

      {mobileFilterOpen && (
        <MobileFilterDrawer
          model={model}
          changeState={changeState}
          search={search}
          onClose={() => setMobileFilterOpen(false)}
        />
      )}

      {mobileSortOpen && (
        <MobileSortDrawer
          model={model}
          changeState={changeState}
          search={search}
          onClose={() => setMobileSortOpen(false)}
        />
      )}
    </div>
  )
}
